import React, { useMemo } from 'react';
import { Box, Hint, OutlinedCompleteIcon, Text, WarningIcon } from '@common-fe/common-fe';
import styled from 'styled-components';

import { Roles } from '@/common/constants';
import { useHasAccess } from '@/modules/core/hooks';
import { LinkedEmployee } from '@/modules/user/user.types';

const EmployerCardWrapper = styled(Box)<{ isDisabled?: boolean }>`
  position: relative;
  cursor: ${({ isDisabled }) => (isDisabled ? 'default' : 'pointer')};
  ${({ theme, isDisabled }) => !isDisabled && theme.shadows.default};

  &:hover {
    ${({ theme, isDisabled }) => !isDisabled && theme.shadows.hover};
  }

  ${({ isDisabled, theme }) => isDisabled ? '' : `
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: ${theme.spacings.spacing8};
    border: ${theme.colors.border2};
  }`}

  &:hover::before {
    border: ${({ theme, isDisabled }) => isDisabled ? 'none' : `2px solid ${theme.colors.border}`};
  }
`;

interface Props {
  linkedEmployee: LinkedEmployee;
  onSwitchingToOrphanMode: (employeeId: string) => void;
  isActiveSession?: boolean;
  testId?: string;
}

const EmployerCard: React.FC<Props> = ({
  linkedEmployee,
  onSwitchingToOrphanMode,
  isActiveSession,
  testId,
}) => {
  const isLimitedAccess = useHasAccess([
    {
      role: Roles.employeeLimitedAccess,
    },
  ]);
  const isDisabled = useMemo(() => isLimitedAccess && !isActiveSession, [isLimitedAccess, isActiveSession]);

  return (
    <EmployerCardWrapper
      pad={{ vertical: 'spacing12', horizontal: 'spacing16' }}
      margin={{ top: 'spacing8' }}
      round="container2Round"
      background="canvas"
      data-testid={`${testId}-employer-card-wrapper`}
      {...isDisabled ? { isDisabled } : {
        onClick: () => onSwitchingToOrphanMode(linkedEmployee?.employeeId),
      }}
    >
      <Box direction="row" justify="between" align="top">
        <Text size="xxlarge" weight={700} style={{ lineHeight: '28px' }} {...isDisabled ? { color: 'textDisabled' } : {}}>
          {linkedEmployee?.organizationName}
        </Text>

        {isActiveSession ? (
          <OutlinedCompleteIcon data-testid="outlined-complete-icon" color="buttonPrimary" />
        ) : null}
        {isDisabled ? (
          <Hint
            testId="orphan-mode-switcher-limited-access-hint"
            contentStyles={{ marginLeft: '-8px' }}
            hintWidth="230px"
            hintAlign="left"
            hintElement={<WarningIcon />}
          >
            <Text textAlign="center" size="small">
              You cannot switch between linked accounts while in limited access. Verify your account to gain full access.
            </Text>
          </Hint>
        ) : null}
      </Box>

      <Box margin={{ top: 'spacing8' }}>
        <Text size="12px" weight={500} {...isDisabled ? { color: 'textDisabled' } : {}}>
          {linkedEmployee?.enrollmentTitle}
        </Text>
      </Box>
    </EmployerCardWrapper>
  );
};

export default EmployerCard;
